import React from 'react';
import { Tooltip } from 'antd';
import {
	ActivePlanStageOverview,
	StageType,
	TreatmentPlanSectionInfo,
} from '../../../../../../types/healthcare/TreatmentPlanTypes';
import { getStageIcon } from './helpers';
import { computeEffectiveStageStatuses } from './dashboardProgress';

// Stage types that carry no user-facing progress meaning are hidden from the icon row.
const HIDDEN_TYPES = new Set(['trigger', 'watcher', 'condition', 'time_condition']);

/** Derives a traffic-light colour for a section dot from its stages' effective statuses. */
const sectionDotColor = (stagesInGroup: ActivePlanStageOverview[], effectiveStatuses: Map<number, string>): string => {
	const statuses = stagesInGroup.map((s) => effectiveStatuses.get(s.snapshot_id) ?? s.status);
	if (statuses.some((s) => s === 'failed'))                          return 'var(--mg-danger, #ff4d4f)';
	if (statuses.some((s) => s === 'in_progress' || s === 'waiting')) return '#fadb14';
	if (statuses.every((s) => s === 'completed'))                      return 'var(--mg-success, #52c41a)';
	return 'var(--mg-border, #d9d9d9)';
};


const stageIconColor = (status: string): string => {
	if (status === 'completed')  return 'var(--mg-success, #52c41a)';
	if (status === 'in_progress') return 'var(--mg-primary, #1890ff)';
	if (status === 'waiting')    return 'var(--mg-warning, #fa8c16)';
	if (status === 'failed')     return 'var(--mg-danger, #ff4d4f)';
	if (status === 'skipped')    return 'var(--mg-text-disabled)';
	return 'var(--mg-border)';
};

const StageIcon: React.FC<{ stage: ActivePlanStageOverview; status?: string; attempt?: boolean }> = ({ stage: s, status, attempt }) => {
	const effectiveStatus = status ?? s.status;
	const baseIcon = getStageIcon(s.stage_type as StageType, 11);
	const icon = React.cloneElement(baseIcon, {
		style: { ...baseIcon.props.style, color: stageIconColor(effectiveStatus), fontSize: 11 },
		spin: effectiveStatus === 'in_progress' ? true : undefined,
	});
	const label = s.title ?? s.stage_key;
	const title = attempt ? `${label} · Attempt ${(s.loop_iteration ?? 0) + 1}` : label;
	return (
		<Tooltip title={title} mouseEnterDelay={0.4}>
			<span style={{ display: 'inline-flex', flexShrink: 0, lineHeight: 1 }}>{icon}</span>
		</Tooltip>
	);
};

/**
 * Renders a treatment plan's stages as a row of status-coloured icons (the peptide
 * dashboard progress system). Shared by the peptide dashboard and the practitioner
 * dashboard so both render progress identically.
 */
const ProgressDots: React.FC<{ stages: ActivePlanStageOverview[]; sections?: TreatmentPlanSectionInfo[] }> = ({ stages, sections }) => {
	const visible = stages.filter((s) => !HIDDEN_TYPES.has(s.stage_type));

	// A completed action whose joined watcher is still waiting should read as waiting (matches the
	// plan modal). Computed from the full stage list (watchers included) before they're hidden.
	const effectiveStatuses = computeEffectiveStageStatuses(stages);

	// A stage_key appearing more than once in the visible row means it was retried (loop attempts);
	// those dots get an "· Attempt N" tooltip suffix. Single-attempt stages stay untouched.
	const stageKeyCounts = new Map<string, number>();
	for (const s of visible) stageKeyCounts.set(s.stage_key, (stageKeyCounts.get(s.stage_key) ?? 0) + 1);
	const isAttempt = (s: ActivePlanStageOverview): boolean => (stageKeyCounts.get(s.stage_key) ?? 0) > 1;

	const sectionMap = new Map((sections ?? []).map((s) => [s.section_key, s]));

	// Group stages preserving order; null key = unsectioned
	const groups: Array<{ key: string | null; stagesInGroup: ActivePlanStageOverview[] }> = [];
	const seen = new Map<string | null, number>();
	for (const stage of visible) {
		const key = stage.section_key ?? null;
		if (seen.has(key)) {
			groups[seen.get(key)!].stagesInGroup.push(stage);
		} else {
			seen.set(key, groups.length);
			groups.push({ key, stagesInGroup: [stage] });
		}
	}

	const hasSections = groups.some((g) => g.key !== null);

	if (!hasSections) {
		// No sections — flat layout (original behaviour)
		return (
			<div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
				{visible.map((s) => <StageIcon key={s.snapshot_id} stage={s} status={effectiveStatuses.get(s.snapshot_id)} attempt={isAttempt(s)} />)}
			</div>
		);
	}

	return (
		<div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
			{groups.map((g) => {
				const sectionInfo = g.key ? sectionMap.get(g.key) : null;
				return (
					<div key={g.key ?? '__none__'} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
						{sectionInfo && (
							<Tooltip title={sectionInfo.name} mouseEnterDelay={0.2}>
								<span
									style={{
										width: 6,
										height: 6,
										borderRadius: '50%',
										background: sectionDotColor(g.stagesInGroup, effectiveStatuses),
										flexShrink: 0,
										cursor: 'default',
									}}
								/>
							</Tooltip>
						)}
						<div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
							{g.stagesInGroup.map((s) => <StageIcon key={s.snapshot_id} stage={s} status={effectiveStatuses.get(s.snapshot_id)} attempt={isAttempt(s)} />)}
						</div>
					</div>
				);
			})}
		</div>
	);
};

export default ProgressDots;
